import { useEffect, useMemo, useState } from "react";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    ExternalLink,
    Loader2,
    Sparkles
} from "lucide-react";
import toast from "react-hot-toast";

import {
    enrichGeneratorPayload
} from "../utils/generatorIntentDefaults";

import {
    createAndQueuePreviewSession,
    getPreviewSession,
    getPreviewSessionStatus,
    claimPreviewSession,
    publishProject,
    getPreviewSessionDocumentUrl,
    getActiveGenerationEngine
} from "../services/websiteService";

import {
    getCurrentUser,
    redirectToSignup,
    redirectToLogin
} from "../services/authService";

import "./MobileGeneratePage.css";

import PublicSupportModal from "../components/support/PublicSupportModal";

const ADMIN_BASE_URL =
    process.env.REACT_APP_ADMIN_URL ||
    "https://admin.shrianshnexus.in";

function getMobileStorageKey(baseKey) {
    const engine = getActiveGenerationEngine();
    return `${baseKey}_${engine}`;
}

function getMobilePreviewSessionKey() {
    return getMobileStorageKey("snx_mobile_preview_session_id");
}

function getMobileClaimedProjectKey() {
    return getMobileStorageKey("snx_mobile_claimed_project_id");
}

const industryOptions = [
    "Fitness",
    "Education",
    "Real Estate",
    "Healthcare",
    "Restaurant",
    "Online Store",
    "Legal",
    "Beauty",
    "SaaS"
];

const ctaOptions = [
    "Book Trial",
    "Contact Us",
    "Get Quote",
    "View Services",
    "Join Program",
    "Buy Online"
];

const styleOptions = [
    "Premium",
    "Modern",
    "Clean",
    "Bold",
    "Luxury"
];

const loadingSteps = [
    "Understanding your business",
    "Planning website pages",
    "Creating CRM modules",
    "Designing mobile-first sections",
    "Preparing your live preview"
];

function getQueryValue(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
}

function saveMobilePreviewSessionId(id) {
    if (!id) return;
    localStorage.setItem(getMobilePreviewSessionKey(), id);
}

function getSavedMobilePreviewSessionId() {
    return (
        getQueryValue("previewSessionId") ||
        localStorage.getItem(getMobilePreviewSessionKey())
    );
}

function saveClaimedProjectId(projectId) {
    if (!projectId) return;
    localStorage.setItem(getMobileClaimedProjectKey(), projectId);
}

function getSavedClaimedProjectId() {
    return localStorage.getItem(getMobileClaimedProjectKey());
}

function inferIndustry(prompt = "") {
    const text = prompt.toLowerCase();

    if (text.includes("fitness") || text.includes("gym") || text.includes("trainer")) return "Fitness";
    if (text.includes("academy") || text.includes("school") || text.includes("course")) return "Education";
    if (text.includes("real estate") || text.includes("property") || text.includes("portal")) return "Real Estate";
    if (text.includes("clinic") || text.includes("doctor") || text.includes("health")) return "Healthcare";
    if (text.includes("restaurant") || text.includes("cafe")) return "Restaurant";
    if (text.includes("store") || text.includes("shop") || text.includes("ecommerce")) return "Online Store";
    if (text.includes("law") || text.includes("legal")) return "Legal";
    if (text.includes("beauty") || text.includes("salon")) return "Beauty";
    if (text.includes("saas") || text.includes("software") || text.includes("app")) return "SaaS";

    return "";
}

function normalizeIndustry(value) {
    if (value === "Online Store") return "Ecommerce / Store";
    if (value === "SaaS") return "SaaS / Tech";
    return value || "Business";
}

function buildBusinessName(description) {
    const clean = String(description || "").trim();

    if (!clean) return "";

    return clean
        .replace(/^we\s+need\s+to\s+build\s+/i, "")
        .replace(/^build\s+/i, "")
        .replace(/^create\s+/i, "")
        .replace(/^a\s+/i, "")
        .split(" ")
        .slice(0, 3)
        .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
        .join(" ");
}

function defaultCtaForIndustry(industry) {
    if (industry === "Fitness") return "Book Trial";
    if (industry === "Real Estate") return "Contact Us";
    if (industry === "Online Store") return "Buy Online";
    if (industry === "Education") return "Join Program";
    return "Contact Us";
}

function buildPayload(form) {
    const industry = normalizeIndustry(form.industry);

    const rawPayload = {
        businessName: form.businessName || buildBusinessName(form.description) || "My Business",
        industry,
        subIndustry: industry,
        description: form.description || "A professional business website generated by Shriansh Nexus.",

        goalOfWebsite: form.callToActionType || "Generate Leads",
        callToActionType: form.callToActionType || "Contact Us",
        brandStyle: form.brandStyle || "Premium",
        tone: "Professional",
        primaryAudience: "Local customers",

        coreServices: form.description,
        projectTypes: form.description,

        pages: [],
        crmModules: [],
        offeredItems: []
    };

    return enrichGeneratorPayload(rawPayload);
}

function resolveProjectId(result) {
    return (
        result?.projectId ||
        result?.claimedProjectId ||
        result?.project?.id ||
        result?.data?.projectId ||
        ""
    );
}

function resolvePreviewSessionId(response) {
    return (
        response?.previewSessionId ||
        response?.id ||
        response?.sessionId ||
        response?.data?.previewSessionId ||
        ""
    );
}

function isGeneratedStatus(status) {
    return (
        status === "Generated" ||
        status === "Completed" ||
        status === "Ready"
    );
}

function isFailedStatus(status) {
    return (
        status === "Failed" ||
        status === "Error" ||
        status === "Expired"
    );
}

export default function MobileGeneratePage() {
    const query = useMemo(() => new URLSearchParams(window.location.search), []);
    const initialPrompt = query.get("prompt") || "";
    const urlPreviewSessionId = query.get("previewSessionId") || "";
    const detectedIndustry = inferIndustry(initialPrompt);

    const [step, setStep] = useState(initialPrompt.trim() ? 1 : 0);
    const [screen, setScreen] = useState(urlPreviewSessionId ? "preview" : "setup");
    const [loadingIndex, setLoadingIndex] = useState(0);
    const [isTyping, setIsTyping] = useState(true);

    const [supportModalOpen, setSupportModalOpen] = useState(false);

    const [form, setForm] = useState({
        description: initialPrompt,
        businessName: buildBusinessName(initialPrompt),
        industry: detectedIndustry,
        callToActionType: defaultCtaForIndustry(detectedIndustry),
        brandStyle: "Premium"
    });

    const [previewSessionId, setPreviewSessionId] = useState(urlPreviewSessionId);
    const [, setPreviewData] = useState(null);
    const [projectId, setProjectId] = useState(getSavedClaimedProjectId() || "");
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const progress =
        screen === "generating"
            ? 88
            : screen === "preview"
                ? 100
                : step === 0
                    ? 20
                    : step === 1
                        ? 55
                        : 78;

    useEffect(() => {
        const savedPreviewSessionId = getSavedMobilePreviewSessionId();

        if (!savedPreviewSessionId) return;

        let cancelled = false;

        async function restorePreviewSession() {
            try {
                setPreviewSessionId(savedPreviewSessionId);
                saveMobilePreviewSessionId(savedPreviewSessionId);

                const preview = await getPreviewSession(savedPreviewSessionId);

                if (cancelled) return;

                setPreviewData(preview);
                setScreen("preview");

                const existingProjectId =
                    resolveProjectId(preview) ||
                    getSavedClaimedProjectId();

                if (existingProjectId) {
                    setProjectId(existingProjectId);
                    saveClaimedProjectId(existingProjectId);
                    return;
                }

                const me = await getCurrentUser().catch(() => null);

                if (cancelled) return;

                if (me) {
                    const claimResult = await claimPreviewSession(savedPreviewSessionId);
                    const claimedProjectId = resolveProjectId(claimResult);

                    if (claimedProjectId) {
                        setProjectId(claimedProjectId);
                        saveClaimedProjectId(claimedProjectId);
                        toast.success("Preview saved to your CRM.");
                    }
                }
            } catch (error) {
                console.error("Failed to restore mobile preview session", error);
                toast.error("Could not restore preview. Please try again.");
                setScreen("setup");
            }
        }

        restorePreviewSession();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (screen !== "setup") return;

        setIsTyping(true);
        const timer = setTimeout(() => setIsTyping(false), 420);

        return () => clearTimeout(timer);
    }, [screen, step]);

    useEffect(() => {
        if (screen !== "generating") return;

        const timer = setInterval(() => {
            setLoadingIndex((prev) => (prev + 1) % loadingSteps.length);
        }, 1350);

        return () => clearInterval(timer);
    }, [screen]);

    const update = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const updateUrlWithPreviewSession = (id) => {
        if (!id) return;

        const engine = getActiveGenerationEngine();
        const url = `/generate/mobile?engine=${encodeURIComponent(engine)}&previewSessionId=${encodeURIComponent(id)}`;
        window.history.replaceState({}, "", url);
    };

    const next = () => {
        if (step === 0) {
            if (!form.description.trim()) {
                toast.error("Describe your business first.");
                return;
            }

            const industry = inferIndustry(form.description);
            setForm((prev) => ({
                ...prev,
                industry: prev.industry || industry,
                businessName: prev.businessName || buildBusinessName(prev.description),
                callToActionType: prev.callToActionType || defaultCtaForIndustry(industry)
            }));

            setStep(1);
            return;
        }

        if (step === 1) {
            if (!form.businessName.trim()) {
                toast.error("Enter your business name.");
                return;
            }

            if (!form.industry) {
                toast.error("Choose business type.");
                return;
            }

            setStep(2);
            return;
        }

        if (step === 2) {
            if (!form.callToActionType) {
                toast.error("Choose the main action.");
                return;
            }

            generate();
        }
    };

    const back = () => {
        if (screen === "preview") {
            setScreen("setup");
            setStep(2);
            return;
        }

        if (step > 0) {
            setStep((prev) => prev - 1);
            return;
        }

        window.history.back();
    };

    const generate = async () => {
        try {
            setScreen("generating");

            localStorage.removeItem(getMobileClaimedProjectKey());
            setProjectId("");

            const response = await createAndQueuePreviewSession(buildPayload(form));
            const id = resolvePreviewSessionId(response);

            if (!id) throw new Error("Preview session not created.");

            setPreviewSessionId(id);
            saveMobilePreviewSessionId(id);
            updateUrlWithPreviewSession(id);

            const interval = setInterval(async () => {
                try {
                    const statusResponse = await getPreviewSessionStatus(id);
                    const status = statusResponse?.status;

                    if (isGeneratedStatus(status)) {
                        clearInterval(interval);

                        const preview = await getPreviewSession(id);
                        setPreviewData(preview);
                        setScreen("preview");
                        toast.success("Preview ready.");
                    }

                    if (isFailedStatus(status)) {
                        clearInterval(interval);
                        setScreen("setup");
                        setStep(2);
                        toast.error(status === "Expired"
                            ? "This preview session has expired."
                            : "Generation failed. Please try again.");
                    }
                } catch (error) {
                    clearInterval(interval);
                    console.error(error);
                    setScreen("setup");
                    setStep(2);
                    toast.error("Could not check preview status.");
                }
            }, 2800);
        } catch (error) {
            console.error(error);
            setScreen("setup");
            setStep(2);
            toast.error("Failed to start preview.");
        }
    };

    const getReturnUrl = () => {
        const id = previewSessionId || getSavedMobilePreviewSessionId();

        if (id) {
            const engine = getActiveGenerationEngine();

            return `${window.location.origin}/generate/mobile?engine=${encodeURIComponent(engine)}&previewSessionId=${encodeURIComponent(id)}`;
        }

        return window.location.href;
    };

    const loginAndReturn = () => {
        if (previewSessionId) {
            saveMobilePreviewSessionId(previewSessionId);
        }

        redirectToLogin(getReturnUrl());
    };

    const signupAndReturn = () => {
        if (previewSessionId) {
            saveMobilePreviewSessionId(previewSessionId);
        }

        redirectToSignup(getReturnUrl());
    };

    const saveToCrm = async () => {
        const id = previewSessionId || getSavedMobilePreviewSessionId();

        if (!id) {
            toast.error("Preview session not found.");
            return;
        }

        try {
            setSaving(true);
            saveMobilePreviewSessionId(id);

            const me = await getCurrentUser().catch(() => null);

            if (!me) {
                signupAndReturn();
                return;
            }

            const result = await claimPreviewSession(id);
            const claimedProjectId = resolveProjectId(result);

            if (claimedProjectId) {
                setProjectId(claimedProjectId);
                saveClaimedProjectId(claimedProjectId);
                toast.success("Saved to CRM.");
                return;
            }

            toast.error("Could not save project.");
        } catch (error) {
            console.error(error);
            toast.error("Could not save preview.");
        } finally {
            setSaving(false);
        }
    };

    const publishSavedProject = async () => {
        if (!projectId) {
            toast.error("Save to CRM first.");
            return;
        }

        try {
            setPublishing(true);
            await publishProject(projectId);
            toast.success("Website published successfully.");
        } catch (error) {
            console.error(error);
            toast.error("Could not publish website.");
        } finally {
            setPublishing(false);
        }
    };

    const openCrm = () => {
        if (!projectId) {
            toast.error("Save to CRM first.");
            return;
        }

        window.location.href = `${ADMIN_BASE_URL}/crm/projects/${projectId}`;
    };

    const previewDocumentUrl = useMemo(() => {
        if (!previewSessionId) return "";

        const baseUrl = getPreviewSessionDocumentUrl(previewSessionId);
        const separator = baseUrl.includes("?") ? "&" : "?";

        return `${baseUrl}${separator}r=${Date.now()}`;
    }, [previewSessionId]);

    return (
        <main className="mgv-page">
            <header className="mgv-header">
                <button type="button" onClick={back}>
                    <ArrowLeft size={19} />
                </button>

                <div className="mgv-brand">
                    <strong>Nexus</strong>
                    <span>AI website builder</span>
                </div>

                <button
                    type="button"
                    className="mgv-help-pill"
                    onClick={() => setSupportModalOpen(true)}
                >
                    Help
                </button>
            </header>

            <div className="mgv-progress-track">
                <span style={{ width: `${progress}%` }} />
            </div>

            {screen === "setup" && (
                <>
                    <section className="mgv-chat">
                        <div className="mgv-ai-message">
                            <div className="mgv-ai-icon">
                                <Sparkles size={16} />
                            </div>

                            <div className="mgv-ai-copy">
                                <span>AI Assistant</span>

                                {isTyping ? (
                                    <div className="mgv-typing">
                                        <i />
                                        <i />
                                        <i />
                                    </div>
                                ) : (
                                    <>
                                        {step === 0 && (
                                            <>
                                                <h1>Tell me what you want to build.</h1>
                                                <p>Describe your idea in one sentence.</p>

                                                <div className="mgv-form-card">
                                                    <label>
                                                        Your idea
                                                        <textarea
                                                            value={form.description}
                                                            onChange={(e) => update("description", e.target.value)}
                                                            placeholder="e.g. Real estate portal for property listings and leads"
                                                        />
                                                    </label>
                                                </div>
                                            </>
                                        )}

                                        {step === 1 && (
                                            <>
                                                <h1>Confirm your business setup.</h1>
                                                <p>
                                                    I detected your business type. You can adjust it before generating.
                                                </p>

                                                <div className="mgv-form-card">
                                                    <label>
                                                        Business name
                                                        <input
                                                            value={form.businessName}
                                                            onChange={(e) => update("businessName", e.target.value)}
                                                            placeholder="e.g. Prime Estate Portal"
                                                        />
                                                    </label>

                                                    <label>
                                                        Business type
                                                        <select
                                                            value={form.industry}
                                                            onChange={(e) => {
                                                                const industry = e.target.value;
                                                                update("industry", industry);
                                                                update("callToActionType", defaultCtaForIndustry(industry));
                                                            }}
                                                        >
                                                            <option value="">Choose business type</option>
                                                            {industryOptions.map((option) => (
                                                                <option key={option} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </label>

                                                    <label>
                                                        Description
                                                        <textarea
                                                            value={form.description}
                                                            onChange={(e) => update("description", e.target.value)}
                                                            placeholder="Describe your business..."
                                                        />
                                                    </label>
                                                </div>
                                            </>
                                        )}

                                        {step === 2 && (
                                            <>
                                                <h1>Choose goal and style.</h1>
                                                <p>This helps Nexus prepare the first website preview.</p>

                                                <div className="mgv-section-label">Main action</div>
                                                <div className="mgv-chip-grid">
                                                    {ctaOptions.map((option) => (
                                                        <button
                                                            key={option}
                                                            type="button"
                                                            className={form.callToActionType === option ? "active" : ""}
                                                            onClick={() => update("callToActionType", option)}
                                                        >
                                                            {option}
                                                        </button>
                                                    ))}
                                                </div>

                                                <div className="mgv-section-label">Website style</div>
                                                <div className="mgv-chip-grid">
                                                    {styleOptions.map((option) => (
                                                        <button
                                                            key={option}
                                                            type="button"
                                                            className={form.brandStyle === option ? "active" : ""}
                                                            onClick={() => update("brandStyle", option)}
                                                        >
                                                            {option}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </section>

                    <div className="mgv-inline-actions">
                        <button type="button" className="ghost" onClick={back}>
                            Back
                        </button>

                        <button type="button" onClick={next} disabled={isTyping}>
                            {step === 2 ? "Generate" : "Next"}
                            <ArrowRight size={17} />
                        </button>
                    </div>
                </>
            )}

            {screen === "generating" && (
                <section className="mgv-generating">
                    <div className="mgv-live-card">
                        <div className="mgv-live-top">
                            <div>
                                <span>Live preview</span>
                                <strong>{buildPayload(form).businessName}</strong>
                            </div>

                            <Loader2 className="mgv-spin" size={20} />
                        </div>

                        <div className="mgv-live-browser">
                            <div className="mgv-live-hero" />
                            <div className="mgv-live-blocks">
                                <span />
                                <span />
                                <span />
                            </div>
                            <div className="mgv-live-lines">
                                <span />
                                <span />
                                <span />
                            </div>
                        </div>
                    </div>

                    <h1>Generating your website</h1>
                    <p>{loadingSteps[loadingIndex]}</p>
                </section>
            )}

            {screen === "preview" && (
                <section className="mgv-preview">
                    <div className="mgv-success">
                        <CheckCircle2 size={26} />
                        <h1>Your preview is ready</h1>
                        <p>
                            {projectId
                                ? "Your website is saved to CRM. You can publish now or edit it in CRM."
                                : "Review the website and save it to your CRM workspace."}
                        </p>
                    </div>

                    <div className="mgv-preview-frame">
                        <iframe
                            title="Website preview"
                            src={previewDocumentUrl}
                        />
                    </div>

                    {!projectId ? (
                        <div className="mgv-preview-actions">
                            <button type="button" onClick={saveToCrm} disabled={saving}>
                                {saving ? "Saving..." : "Save to CRM"}
                            </button>

                            <button
                                type="button"
                                className="ghost"
                                onClick={signupAndReturn}
                            >
                                Create account
                            </button>

                            <button
                                type="button"
                                className="ghost"
                                onClick={loginAndReturn}
                            >
                                Login
                            </button>
                        </div>
                    ) : (
                        <div className="mgv-preview-actions">
                            <button type="button" onClick={publishSavedProject} disabled={publishing}>
                                {publishing ? "Publishing..." : "Publish site"}
                            </button>

                            <button type="button" className="ghost" onClick={openCrm}>
                                <ExternalLink size={16} />
                                Open CRM
                            </button>
                        </div>
                    )}
                </section>
            )}

            <PublicSupportModal
                open={supportModalOpen}
                onClose={() => setSupportModalOpen(false)}
                previewSessionId={previewSessionId}
                businessName={form.businessName}
                industry={form.industry}
            />
        </main>
    );
}